import { useState, useEffect } from 'react';
import { Receipt, TrendingUp, TrendingDown, Clock, Filter } from 'lucide-react';
import { api } from '../api/client';

const STATUS_CONFIG = {
  OPEN:    { color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/20' },
  PARTIAL: { color: 'text-blue-400',  bg: 'bg-blue-400/10',  border: 'border-blue-400/20'  },
  CLOSED:  { color: 'text-slate-400', bg: 'bg-slate-400/10', border: 'border-slate-400/20' },
};

const RESULT_CONFIG = {
  WIN:  { color: 'text-green-400' },
  LOSS: { color: 'text-red-400'   },
  BE:   { color: 'text-slate-400' },
};

function PnlBadge({ pnl }) {
  if (pnl == null) return <span className="text-slate-600 font-mono text-xs">—</span>;
  const val = parseFloat(pnl);
  const color = val > 0 ? 'text-green-400' : val < 0 ? 'text-red-400' : 'text-slate-400';
  return (
    <span className={`font-mono text-xs font-bold ${color}`}>
      {val > 0 ? '+' : ''}{val.toFixed(2)}
    </span>
  );
}

function TradeRow({ trade }) {
  const [expanded, setExpanded] = useState(false);
  const statusCfg = STATUS_CONFIG[trade.status] || STATUS_CONFIG.CLOSED;
  const resultCfg = RESULT_CONFIG[trade.result] || {};
  const isBuy = trade.tradeType?.includes('BUY');

  const openedAt = trade.openedAt
    ? new Date(trade.openedAt).toLocaleString('es-MX', {
        timeZone: 'America/Mexico_City',
        month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      })
    : '—';

  return (
    <div className={`bg-[#13131A] border rounded transition-all duration-150 ${
      expanded ? 'border-amber-500/20' : 'border-[#1E1E2E]'
    }`}>
      <div
        className="grid grid-cols-12 items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02] text-xs font-mono"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Símbolo + tipo */}
        <div className="col-span-2 flex items-center gap-2">
          {isBuy
            ? <TrendingUp size={12} className="text-green-400 shrink-0" />
            : <TrendingDown size={12} className="text-red-400 shrink-0" />
          }
          <div>
            <p className="text-slate-200 font-bold">{trade.symbol}</p>
            <p className={`text-[10px] ${isBuy ? 'text-green-400' : 'text-red-400'}`}>
              {trade.tradeType?.replace('ORDER_TYPE_', '') || '—'}
            </p>
          </div>
        </div>

        {/* Status */}
        <div className="col-span-1">
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${statusCfg.color} ${statusCfg.bg} ${statusCfg.border}`}>
            {trade.status}
          </span>
        </div>

        {/* Entry */}
        <div className="col-span-2 text-slate-400">
          <p className="text-[10px] text-slate-600">Entry</p>
          <p>{trade.entryPrice ? parseFloat(trade.entryPrice).toFixed(2) : '—'}</p>
        </div>

        {/* SL / TP */}
        <div className="col-span-2 text-slate-500">
          <p className="text-[10px] text-slate-600">SL / TP</p>
          <p>
            <span className="text-red-400/70">{trade.slPrice ? parseFloat(trade.slPrice).toFixed(2) : '—'}</span>
            <span className="text-slate-600 mx-1">/</span>
            <span className="text-green-400/70">{trade.tpPrice ? parseFloat(trade.tpPrice).toFixed(2) : '—'}</span>
          </p>
        </div>

        {/* PnL */}
        <div className="col-span-2">
          <p className="text-[10px] text-slate-600">PnL</p>
          <PnlBadge pnl={trade.totalPnl} />
          {trade.result && (
            <span className={`text-[10px] ml-1 ${resultCfg.color || 'text-slate-500'}`}>
              {trade.result}
            </span>
          )}
        </div>

        {/* Source */}
        <div className="col-span-1">
          <span className={`text-[10px] ${trade.source === 'MANUAL' ? 'text-purple-400' : 'text-slate-600'}`}>
            {trade.source || 'AGENT'}
          </span>
        </div>

        {/* Timestamp */}
        <div className="col-span-2 text-right text-slate-600 text-[10px]">
          <Clock size={9} className="inline mr-1" />
          {openedAt}
        </div>
      </div>

      {/* Detalle expandido */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-[#1E1E2E] pt-3 space-y-3">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Volumen</p>
              <p className="text-xs font-mono text-slate-300">{trade.volume || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Duración</p>
              <p className="text-xs font-mono text-slate-300">
                {trade.durationMinutes ? `${trade.durationMinutes} min` : '—'}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Macro Bias</p>
              <p className="text-xs font-mono text-slate-300">{trade.dailyMacroBias || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Breakeven</p>
              <p className={`text-xs font-mono ${trade.slMovedToBreakeven ? 'text-green-400' : 'text-slate-600'}`}>
                {trade.slMovedToBreakeven ? 'Sí' : 'No'}
              </p>
            </div>
          </div>
          {trade.traderNotes && (
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Notas del trader</p>
              <p className="text-xs font-mono text-slate-400 bg-[#0A0A0F] rounded p-2">{trade.traderNotes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Trades() {
  const [trades, setTrades]   = useState([]);
  const [filter, setFilter]   = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.getTrades({ limit: 50, status: filter === 'ALL' ? undefined : filter })
      .then(r => setTrades(r.data))
      .catch(() => setError('No se pudieron cargar los trades'))
      .finally(() => setLoading(false));
  }, [filter]);

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="flex items-center gap-3 text-amber-500 font-mono text-sm">
        <div className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
        Cargando trades...
      </div>
    </div>
  );

  const totalPnl = trades.reduce((acc, t) => acc + (parseFloat(t.totalPnl) || 0), 0);
  const wins     = trades.filter(t => t.result === 'WIN').length;
  const losses   = trades.filter(t => t.result === 'LOSS').length;

  return (
    <div className="p-8 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-100 tracking-tight">Trades</h1>
          <p className="text-xs text-slate-500 font-mono mt-1">{trades.length} trades · {wins}W / {losses}L</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-slate-500">PnL total:</span>
          <PnlBadge pnl={totalPnl} />
        </div>
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-2">
        <Filter size={12} className="text-slate-600" />
        {['ALL', 'OPEN', 'CLOSED', 'PARTIAL'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-[11px] font-mono px-3 py-1 rounded border transition-all ${
              filter === f
                ? 'border-amber-500/30 bg-amber-500/10 text-amber-400'
                : 'border-[#1E1E2E] text-slate-600 hover:text-slate-400'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-red-400 text-xs font-mono border border-red-400/20 bg-red-400/5 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {trades.length === 0 && !error && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Receipt size={32} className="text-slate-700 mx-auto mb-3" />
            <p className="text-slate-600 font-mono text-sm">Sin trades registrados aún</p>
          </div>
        </div>
      )}

      {/* Tabla */}
      <div className="space-y-2">
        {trades.map(t => (
          <TradeRow key={t.id} trade={t} />
        ))}
      </div>

    </div>
  );
}